import { useState } from "react";
import styles from "./MobilePaymentAndFees.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import PaymentFilterGroup from "./components/PaymentFilterGroup";
import PaymentChevronDown from "@Icons/PaymentChevronDown";
import OfferChevronRight from "@Icons/OfferChevronRight";
import RadioButton from "@Components/Radio";
import Portal from "@Components/Portal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import PropertyDetailsModal from "../PropertyDetailsModal";
import getPaymentPolicyIcon from "./utils/getPaymentPolicyIcon";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";

interface MobilePaymentAndFeesProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  selectedDuration: string;
  setSelectedDuration: (duration: string) => void;
  setSelectedYear: (year: string) => void;
  durations: string[];
  years: string[];
  displayPlans: any[];
  allCount: number;
  withGuarantorCount: number;
  withoutGuarantorCount: number;
  paymentPolicies: any[]; // Changed from individual policies to array
  hasInstallmentPlans?: boolean;
}

export default ({
  selectedTab,
  setSelectedTab,
  selectedDuration,
  setSelectedDuration,
  setSelectedYear,
  durations,
  years,
  displayPlans,
  allCount,
  withGuarantorCount,
  withoutGuarantorCount,
  paymentPolicies,
  hasInstallmentPlans = false,
}: MobilePaymentAndFeesProps) => {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [tempSelectedDuration, setTempSelectedDuration] = useState<string>("");
  const [openModal, setOpenModal] = useState<string | null>(null);
  const { t } = useTranslationHook();
  const isDesktop = useIsDesktop();

  const handlePlanToggle = (index: number) => {
    setExpandedPlan(expandedPlan === index ? null : index);
  };

  const openPolicyModal = (sectionKey: string) => {
    setOpenModal(sectionKey);
  };

  const closePolicyModal = () => {
    setOpenModal(null);
  };

  const handleDurationSelect = (duration: string) => {
    setTempSelectedDuration(duration);
  };

  const resetDuration = () => {
    setTempSelectedDuration("");
    setSelectedDuration("All");
    setShowDurationModal(false);
  };

  const applyDurationFilter = () => {
    setSelectedDuration(tempSelectedDuration || "All");
    setShowDurationModal(false);
  };

  const onClickDuration = () => {
    setTempSelectedDuration(selectedDuration === "All" ? "" : selectedDuration);
    setShowDurationModal(true);
  };

  const getGuarantorType = (plan: any) => {
    const type = plan.meta?.guarantor_required;
    switch (type) {
      case "local_without_third_party":
        /* "Local guarantors only (third party guarantor service not allowed)" */
        return t("propertypage.paymentandfees.guarantor.localNoThirdParty");
      case "local_with_third_party":
        /* "Local guarantors only (third party guarantor service allowed)" */
        return t("propertypage.paymentandfees.guarantor.localWithThirdParty");
      case "international_local":
        /* "International and local guarantors allowed" */
        return t("propertypage.paymentandfees.guarantor.internationalLocal");
      case "false":
        /* "No guarantor required" */
        return t("propertypage.paymentandfees.guarantor.none");
      case "information_unavailable":
        /* "Information unavailable" */
        return t("propertypage.paymentandfees.guarantor.unavailable");
      default:
        /* "Guarantor information not available" */
        return plan.notes?.note || t("propertypage.paymentandfees.guarantor.notAvailable");
    }
  };

  const getPlanTitle = (plan: any) => {
    if (plan.plan_type === "fixed") {
      return plan.number_of_installments === 0
        ? "1 Instalment Plan"
        : `${plan.number_of_installments} Instalment Plan`;
    }
    return "Installment Plan";
  };

  // Helper function to check if policy should show payment icons
  const shouldShowPaymentIcons = (tag: string) => tag === "mode_of_payment";

  // Helper function to strip HTML tags and get plain text
  const stripHtmlTags = (html: string) => {
    if (typeof document === "undefined") {
      return html.replace(/<[^>]*>?/gm, "");
    }
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <>
      <div className={styles.mobileContainer} id="payment_and_fees">
        <div className={styles.mobileSectionHeader}>
          <h2 className={styles.mobileHeaderTitle}>
            {/* "Payment and Fees" */}
            {t("propertypage.paymentandfees.header")}
          </h2>
        </div>

        {hasInstallmentPlans && (
          <>
            <div className={styles.mobileFilterSection}>
              <div className={styles.mobileFilterRow}>
                <PaymentFilterGroup
                  filtersData={{ duration: durations, year: years }}
                  setDuration={setSelectedDuration}
                  setYear={setSelectedYear}
                />

                {durations.length > 0 && (
                  <div
                    className={formatClassNames(
                      styles.mobileDurationFilter,
                      selectedDuration !== "All" && styles.selected,
                    )}
                    onClick={onClickDuration}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onClickDuration();
                      }
                    }}
                  >
                    {selectedDuration !== "All"
                      ? selectedDuration
                      : /* "Duration" */
                        t("propertypage.paymentandfees.filters.duration")}
                    <div
                      className={formatClassNames(
                        styles.mobilePillIcon,
                        selectedDuration !== "All" && styles.selected,
                      )}
                    >
                      <PaymentChevronDown />
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.mobileTabsContainer}>
                <button
                  type="button"
                  className={formatClassNames(
                    styles.mobileTab,
                    selectedTab === "all" && styles.mobileTabActive,
                  )}
                  onClick={() => setSelectedTab("all")}
                >
                  {t("propertypage.paymentandfees.all")} ({allCount})
                </button>
                {withGuarantorCount > 0 && (
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.mobileTab,
                      selectedTab === "with_guarantor" && styles.mobileTabActive,
                    )}
                    onClick={() => setSelectedTab("with_guarantor")}
                  >
                    {t("propertypage.paymentandfees.withguarantor")} ({withGuarantorCount})
                  </button>
                )}
                {withoutGuarantorCount > 0 && (
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.mobileTab,
                      selectedTab === "without_guarantor" && styles.mobileTabActive,
                    )}
                    onClick={() => setSelectedTab("without_guarantor")}
                  >
                    {t("propertypage.paymentandfees.withoutguarantor")} ({withoutGuarantorCount})
                  </button>
                )}
              </div>
            </div>

            <div className={styles.mobileContent}>
              <div className={styles.mobilePlansList}>
                {displayPlans.map((plan: any, index: number) => (
                  <div key={index} className={styles.mobilePlanCard}>
                    <div
                      className={styles.mobilePlanHeader}
                      onClick={() => handlePlanToggle(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handlePlanToggle(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.mobilePlanInfo}>
                        <div className={styles.mobilePlanTitle}>{getPlanTitle(plan)}</div>
                        <div className={styles.mobilePlanSubtitle}>
                          {plan.duration} {plan.duration_unit} • {getGuarantorType(plan)}
                        </div>
                      </div>
                      <div
                        className={formatClassNames(
                          styles.mobileChevron,
                          expandedPlan === index && styles.mobileChevronExpanded,
                        )}
                      >
                        <PaymentChevronDown />
                      </div>
                    </div>

                    {expandedPlan === index &&
                      plan.plan_installments &&
                      plan.plan_installments.length > 0 && (
                        <div className={styles.mobilePlanDetails}>
                          {plan.plan_installments.map((installment: any, idx: number) => (
                            <div key={idx} className={styles.mobileInstallmentRow}>
                              <div className={styles.mobileInstallmentColumn}>
                                <div className={styles.mobileLabel}>Rent</div>
                                <div className={styles.mobileValue}>
                                  {/* "Instalment {idx + 1}" */}
                                  {t("propertypage.paymentandfees.table.instalment", {
                                    idx: String(idx + 1),
                                  })}
                                </div>
                              </div>
                              <div className={styles.mobileInstallmentColumn}>
                                <div className={styles.mobileLabel}>Pay Before</div>
                                <div className={styles.mobileValue}>
                                  {new Date(installment.due_date).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                              </div>
                              <div className={styles.mobileInstallmentColumn}>
                                <div className={styles.mobileLabel}>Amount Due</div>
                                <div className={styles.mobileValue}>
                                  {installment.installment_amount}%
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}

                <div className={styles.mobileNoteBox}>
                  <span className={styles.mobileNoteText}>
                    {/* "Note: The amount shown is a percentage of the total. Actual amounts may vary." */}
                    <strong>{t("propertypage.paymentandfees.note.label")}:</strong>{" "}
                    {t("propertypage.paymentandfees.note.percentageDisclaimer")}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        <div className={styles.mobileInfoSection}>
          {hasInstallmentPlans && paymentPolicies && paymentPolicies.length > 0 && (
            <div className={styles.mobileDivider} />
          )}
          {paymentPolicies &&
            paymentPolicies.length > 0 &&
            paymentPolicies.map((policy: any, index: number) => (
              <>
                {index > 0 && (
                  <div key={`divider-${policy.tag}-${index}`} className={styles.mobileDivider} />
                )}

                <div
                  key={`${policy.tag}-${index}`}
                  className={styles.mobileInfoRow}
                  onClick={() => policy.value && openPolicyModal(policy.tag)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && policy.value) {
                      openPolicyModal(policy.tag);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={policy.value ? { cursor: "pointer" } : undefined}
                >
                  <div className={styles.mobileInfoContent}>
                    <div className={styles.mobileTitleRow}>
                      <div className={styles.mobileIcon}>
                        {getPaymentPolicyIcon(policy?.tag_en ?? policy.tag)}
                      </div>
                      <h3 className={styles.mobileInfoTitle}>
                        {policy.display_name || policy.tag}
                      </h3>
                    </div>
                    {policy.value && (
                      <div className={styles.mobileInfoTextIndented}>
                        {stripHtmlTags(policy.value)}
                      </div>
                    )}
                    {shouldShowPaymentIcons(policy?.tag_en ?? policy.tag) && (
                      <div className={styles.mobilePaymentIcons}>
                        <div className={styles.mobilePaymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#003087" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              PayPal
                            </text>
                          </svg>
                        </div>
                        <div className={styles.mobilePaymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#1A1F71" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              VISA
                            </text>
                          </svg>
                        </div>
                        <div className={styles.mobilePaymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#EB001B" />
                            <circle cx="15" cy="12" r="6" fill="#FF5F00" />
                            <circle cx="25" cy="12" r="6" fill="#F79E1B" />
                          </svg>
                        </div>
                        <div className={styles.mobilePaymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#016FD0" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="bold"
                            >
                              AMEX
                            </text>
                          </svg>
                        </div>
                      </div>
                    )}
                    {policy.value && (
                      <button
                        type="button"
                        className={styles.mobileViewMoreButton}
                        onClick={() => openPolicyModal(policy.tag)}
                      >
                        {t("View more")}
                        <span className={styles.mobileArrow}>
                          <OfferChevronRight width={12} height={12} color="#111928" />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            ))}
        </div>
      </div>

      {showDurationModal && durations.length > 0 && (
        <Portal rootId="forPortal">
          <BottomSheetOverlay
            hideBottomSheetOverlay={() => setShowDurationModal(false)}
            isHistoryPush={!isDesktop}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.filterMobileHeading}>
                {/* "Duration" */}
                {t("propertypage.paymentandfees.filters.duration")}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowDurationModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {durations.map((duration) => (
                <div
                  className={styles.modalItem}
                  key={`duration-modal-${duration}`}
                  onClick={() => handleDurationSelect(duration)}
                  role="none"
                >
                  <RadioButton isSelected={duration === tempSelectedDuration} />
                  <span className={styles.filterMobileModalText}>{duration}</span>
                </div>
              ))}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={formatClassNames(
                  styles.clearButton,
                  !tempSelectedDuration && styles.inactive,
                )}
                onClick={() => {
                  if (tempSelectedDuration) resetDuration();
                }}
                type="button"
              >
                Clear
              </button>
              <button className={styles.applyButton} onClick={applyDurationFilter} type="button">
                Apply
              </button>
            </div>
          </BottomSheetOverlay>
        </Portal>
      )}

      {/* Policy Modals - Dynamic */}
      {paymentPolicies &&
        paymentPolicies.map(
          (policy: any) =>
            openModal === policy.tag &&
            policy.value && (
              <PropertyDetailsModal
                key={policy.tag}
                header={policy.display_name || policy.tag}
                actionButtonText="Got it"
                hidePropertyModal={closePolicyModal}
                isDesktop={false}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: replaceAmberDomainInLinks(policy.value),
                  }}
                />
              </PropertyDetailsModal>
            ),
        )}
    </>
  );
};
